import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useInfiniteJobs } from '~/domain/Jobs/useCases/useInfiniteJobs';
import { useUserSelector } from '~/hooks/useUserSelector';
import { IMultipleCounters, WorkerClassificationTypeEnum } from '~/models';

export const useCandidateJobs = (candidateId: string, officeId: string) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { workerClassification } = useUserSelector();

  const isContractor =
    workerClassification?.classificationType === WorkerClassificationTypeEnum.Contractor;
  const isEmployee =
    workerClassification?.classificationType === WorkerClassificationTypeEnum.Employee;

  const {
    jobs,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isSuccess,
    ...queryProps
  } = useInfiniteJobs({
    professionalId: candidateId,
    officeId,
    status: 'pending-counter',
    enabled: !!candidateId && !!officeId,
  });

  const candidate = useMemo(() => {
    if (!jobs.length) return null;

    const counter = jobs[0].multipleCounters.find(
      (counter) => counter.candidate.id === candidateId,
    ) as IMultipleCounters;

    return {
      candidate: counter.candidate,
      hasWorkedWithDSO: counter?.hasWorkedWithDSO || false,
      hasWorkedWithOffice: counter?.hasWorkedWithOffice || false,
      backgroundCheckStatus: counter?.requestor?.backgroundCheck?.status,
    };
  }, [jobs, candidateId]);

  useEffect(() => {
    if (!jobs.length && isSuccess) {
      navigate('/');
      queryClient.removeQueries({ queryKey: ['jobs-infinite'] });
    }
  }, [jobs, isSuccess, navigate, queryClient]);

  return {
    candidate,
    jobs,
    isContractor,
    isEmployee,
    isFirstLoading: isLoading,
    isGetJobsLoading: isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    ...queryProps,
  };
};
